/**
 * VideoTile: JitsiTrack을 HTML <video>에 attach/detach하는 핵심 컴포넌트.
 *
 * wehago-conference SVContent/MainVideo/Video/VideoContainer.js 패턴 기반.
 *
 * 안전 규칙 (Chrome 146 CVE):
 * - track.disposed !== false 이면 attach 금지
 * - track.stream / track.track 직접 접근 금지
 * - 트랙 교체 시: detach → srcObject=null → requestAnimationFrame → attach
 * - 언마운트 시: detach → srcObject=null (dispose는 상위에서)
 */
import { useEffect, useRef, useCallback } from 'react';
import type { JitsiLocalTrack, JitsiRemoteTrack } from '@/types/jitsi';

// lib-jitsi-meet 내부 containers 프로퍼티 타입 확장
type TrackWithContainers = (JitsiLocalTrack | JitsiRemoteTrack) & {
  containers?: HTMLElement[];
};

interface VideoTileProps {
  track: JitsiLocalTrack | JitsiRemoteTrack | null;
  isLocal?: boolean;
  isDesktop?: boolean;
  isReverse?: boolean;
  className?: string;
  style?: React.CSSProperties;
  /** 비디오 첫 프레임 수신 여부 변경 시 호출 (true: 프레임 있음, false: 아직 없음) */
  onReady?: (ready: boolean) => void;
}

const VideoTile: React.FC<VideoTileProps> = ({
  track,
  isLocal = false,
  isDesktop = false,
  isReverse = false,
  className = '',
  style,
  onReady,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const prevTrackRef = useRef<TrackWithContainers | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  // loadeddata: 첫 프레임이 디코딩되어 표시 가능할 때 발생
  const handleLoadedData = useCallback(() => {
    onReadyRef.current?.(true);
  }, []);

  useEffect(() => {
    const container = videoRef.current;
    if (!container) return;

    const prevTrack = prevTrackRef.current;
    const currentTrack = track as TrackWithContainers | null;

    // 같은 트랙이면 무시
    if (currentTrack === prevTrack) return;
    prevTrackRef.current = currentTrack;

    // 트랙 교체 시 ready 상태 리셋
    onReadyRef.current?.(false);

    // 진행 중인 rAF 취소
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    // 이전 트랙 detach (disposed === false 인 경우만)
    if (prevTrack && prevTrack.disposed === false) {
      try {
        prevTrack.detach(container);
      } catch { /* ignore */ }
    }
    container.srcObject = null;

    if (!currentTrack) return;

    const doAttach = () => {
      if (!currentTrack || currentTrack.disposed !== false) return;
      if (currentTrack.containers?.includes(container)) return;
      try {
        currentTrack.attach(container);
        // 비디오 엘리먼트의 audio는 항상 mute (오디오 트랙은 ParticipantAudio에서 별도 처리)
        container.volume = 0.0;
        // 이미 프레임이 있으면 즉시 ready 콜백 (loadeddata가 재발화하지 않는 케이스 대응)
        if (container.readyState >= 2) {
          onReadyRef.current?.(true);
        }
      } catch { /* ignore */ }
    };

    if (prevTrack) {
      // 트랙 교체: Chrome 디코더 정리를 위해 rAF 딜레이
      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;
        doAttach();
      });
    } else {
      // 최초 마운트: 즉시 attach
      doAttach();
    }
  }, [track]);

  // 언마운트 시 정리
  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
      const container = videoRef.current;
      const t = prevTrackRef.current;
      if (container && t && t.disposed === false) {
        try { t.detach(container); } catch { /* ignore */ }
        container.srcObject = null;
      }
    };
  }, []);

  // 로컬 비디오 좌우반전 (rtcCustom.css가 비활성이므로 inline style로 처리)
  // - 로컬 + 일반 카메라: 기본 미러(rotateY 180deg), isReverse 시 해제
  // - 로컬 + 데스크톱: 기본 미러 없음, isReverse 시 미러
  const shouldMirror = isLocal && (isDesktop === isReverse);

  const cls = [
    'video',
    isDesktop ? 'is_desktop' : '',
    isReverse ? 'is_reverse' : '',
    isLocal ? 'is_me' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <video
      ref={videoRef}
      className={cls}
      autoPlay
      playsInline
      // 비디오 엘리먼트 자체 오디오는 mute (ParticipantAudio에서 별도 재생)
      muted
      onLoadedData={handleLoadedData}
      style={{
        width: '100%',
        height: '100%',
        backgroundColor: '#000',
        objectFit: isDesktop ? 'contain' : 'cover',
        transform: shouldMirror ? 'rotateY(180deg)' : undefined,
        ...style,
      }}
    />
  );
};

export default VideoTile;
